import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTheme } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import Joyride, { STATUS, ACTIONS, EVENTS } from "react-joyride";

import {
  walkthroughSelect,
  showFeatureDetailsKeySelect,
  showStartGuideModalSelect,
  showCompletionModalSelect,
} from "Layout/slice/selector";
import {
  toggleGetStartedPanel,
  toggleGetStartedToggleButton,
  setWalkthrough,
} from "Layout/slice";

import { Tooltip } from "./Tooltip";
import { USER_FLAGS } from "utils/constants";
import { getWalkthroughSteps } from "./walkthrough";
import { CompletionModal } from "../CompletionModal";
import { updateUserFlags } from "Layout/slice/thunk";
import { setShowFeatureDetailsKey } from "Layout/slice";
import { FeatureDetailsModal } from "../FeatureDetailsModal";
import { toggleCompletionModal } from "Layout/slice/index";

export const Walkthrough = () => {
  const theme = useTheme();

  const dispatch = useDispatch();
  const featureKey = useSelector(showFeatureDetailsKeySelect);
  const showStartGuideModal = useSelector(showStartGuideModalSelect);
  const showCompletionModal = useSelector(showCompletionModalSelect);
  const { run, stepIndex } = useSelector(walkthroughSelect);

  const [steps, setSteps] = useState([]);

  useEffect(() => {
    if (
      featureKey === "welcome" ||
      featureKey === "welcomeInvest" ||
      featureKey === "investStrategy" ||
      featureKey === "allocation" ||
      featureKey === "startStrategy"
    ) {
      const timer = setTimeout(() => {
        dispatch(
          setWalkthrough({
            run: true,
            stepIndex: 0,
          })
        );
        setSteps(getWalkthroughSteps(theme)[featureKey]);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [featureKey]);

  const handleCallback = (data) => {
    const { status, action, type, index } = data;

    if (
      // If close button clicked, then close the tour
      action === ACTIONS.CLOSE ||
      // If skipped or end tour, then close the tour
      (status === STATUS.SKIPPED && run) ||
      status === STATUS.FINISHED
    ) {
      if (featureKey === USER_FLAGS.HAS_STRATEGIES_EXPLORED) {
        dispatch(updateUserFlags(USER_FLAGS.HAS_STRATEGIES_EXPLORED));
      }

      handleHide();
    } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      // Check whether next or back button click and update the step.
      dispatch(
        setWalkthrough({
          run: true,
          stepIndex: index + (action === ACTIONS.PREV ? -1 : 1),
        })
      );
    }
  };

  const handleStart = () => {
    dispatch(
      setWalkthrough({
        run: true,
        stepIndex: 0,
      })
    );
    const featureSteps = getWalkthroughSteps(theme);
    setSteps(featureSteps[featureKey]);
  };

  const handleHide = () => {
    dispatch(
      setWalkthrough({
        run: false,
        stepIndex: 0,
      })
    );
    setSteps([]);
    dispatch(toggleGetStartedPanel(false));
    dispatch(toggleGetStartedToggleButton(true));
    dispatch(setShowFeatureDetailsKey(""));
  };

  return (
    <>
      <Joyride
        stepIndex={stepIndex}
        continuous
        run={run}
        tooltipComponent={Tooltip}
        steps={steps}
        styles={{
          options: {
            zIndex: 9999,
            arrowColor: theme.button.bg.primary,
          },
          spotlight: {
            backgroundColor: theme.walkthrough.bg.primary,
          },
          floaterStyles: {
            arrow: {
              length: 14,
              spread: 18,
            },
          },
        }}
        spotlightPadding={4}
        callback={handleCallback}
        scrollOffset={200}
        spotlightClicks={true}
        nonce="walkthrough"
        locale={{
          last: "Got it",
        }}
      />

      {showStartGuideModal && (
        <FeatureDetailsModal
          show={showStartGuideModal}
          handleStart={handleStart}
          handleHide={handleHide}
        />
      )}

      {showCompletionModal && (
        <CompletionModal
          show={showCompletionModal}
          onHide={() => dispatch(toggleCompletionModal(false))}
        />
      )}
    </>
  );
};

Walkthrough.propTypes = {
  run: PropTypes.number,
  steps: PropTypes.array,
  handleClose: PropTypes.func,
};
